import { Calendar, ChevronRight, Globe, Phone } from "lucide-react";
import "./applicantCard.css";
import { describeApplicationStatus, describeEkycProcessStatus, describeInviteSourceStatus, formatDateTh, LINE_MEMBERSHIP_TAG } from "./applicantShared.js";

/**
 * Two badges: a constant "Quick Register" origin tag (this record's real
 * source, Prompt 091's public quick-register flow - true regardless of
 * current status) plus the real, live status badge derived from
 * applicationStatus (Prompt 138W-R5) - never the backend's own semi-stale
 * stage.code/stage.label fields. Amber/blue/green only - never red, since a
 * pipeline candidate (even one needing correction or rejected) is a normal
 * pipeline state here, not an error the Agent caused.
 */
export function ApplicantStatusBadges({ item }) {
  const status = describeApplicationStatus(item.applicationStatus);
  return (
    <div className="wx-applicant-stage-badges">
      <span className="wx-badge wx-badge--info">Quick Register</span>
      <span className={`wx-badge wx-badge--${status.badgeVariant}`}>{status.label}</span>
    </div>
  );
}

/**
 * Prompt 138W-R4/R5, renamed by Prompt 138Y-C: the Agent Workers page's own
 * operational-density card - full contact/invite-card detail, because
 * "manage my roster" is this page's actual job (Prompt 138Y-C requirement:
 * Workers must show "operational detail," not a glance summary). This is
 * the one surface that keeps the full field set; Dashboard and Org Chart
 * deliberately do not duplicate it (see ApplicantDashboardItem/
 * ApplicantOrgRow below) - three different components for three different
 * jobs, not one generic card reused everywhere. Prompt 138Y-D: the
 * next-step line now reads from describeEkycProcessStatus (the same single
 * source of truth every other surface and the drawer use), not a separate
 * "next step" phrasing - so the card and the drawer it opens always agree.
 */
export function ApplicantOperationalCard({ item, onClick }) {
  return (
    <div className="wx-applicant-card" onClick={onClick}>
      <div className="wx-applicant-card-top">
        <div>
          <strong>{item.fullNameNative || item.fullNameEnglish || "-"}</strong>
          <span className="wx-applicant-card-sub">{item.applicationId}</span>
        </div>
        <ApplicantStatusBadges item={item} />
      </div>
      <div className="wx-applicant-card-grid">
        <div>
          <label>ติดต่อ</label>
          <strong>{item.phoneNumber || "-"}</strong>
        </div>
        <div>
          <label>สัญชาติ / ภาษา</label>
          <strong>
            {item.nationality || "-"}
            {item.preferredLanguageCode ? ` / ${item.preferredLanguageCode}` : ""}
          </strong>
        </div>
        <div>
          <label>Invite Card</label>
          <strong>{item.inviteCard?.inviteCodeMasked || "-"}</strong>
        </div>
        <div>
          <label>สร้างเมื่อ</label>
          <strong>{formatDateTh(item.createdAtUtc) ?? "-"}</strong>
        </div>
        {/* Prompt 139B: only present for an applicant who submitted via the
            real Prompt 139A document-submit route - null/false for anything
            older, so these two cells simply don't render rather than
            showing a misleading "-". */}
        {item.skill?.name && (
          <div>
            <label>ความถนัด</label>
            <strong>{item.skill.name}</strong>
          </div>
        )}
        {item.passportNumberMasked && (
          <div>
            <label>Passport</label>
            <strong>{item.passportNumberMasked}</strong>
          </div>
        )}
        {item.passportNumberMasked && (
          <div>
            <label>เอกสารประจำตัว</label>
            <strong>{item.hasPassportDocument ? "อัปโหลดแล้ว" : "ยังไม่อัปโหลด"}</strong>
          </div>
        )}
      </div>
      {/* Prompt 139C: purely informational - a revoked/expired source link
          is never a reason this applicant disappears from the roster. */}
      {describeInviteSourceStatus(item.inviteCard?.sourceStatus) && (
        <p className="wx-applicant-invite-source-note">{describeInviteSourceStatus(item.inviteCard?.sourceStatus)}</p>
      )}
      <p className="wx-applicant-next-step">{describeEkycProcessStatus(item.applicationStatus)}</p>
      <div className="wx-applicant-card-footer">
        <span>อยู่ในสายงานแล้ว · ยังไม่เป็น Worker Profile สมบูรณ์</span>
        <span className="wx-applicant-card-affordance">ดูรายละเอียด →</span>
      </div>
    </div>
  );
}

// Prompt 139B scoping note: skill/masked-passport/document-status are shown
// on ApplicantOperationalCard (above, the Workers page's full-detail card)
// and in the shared ApplicantDetailContent drawer (ApplicantDetailDrawer.jsx)
// only - deliberately not added to ApplicantOrgRow or ApplicantDashboardItem
// below. Both of those are already established as glance-density surfaces
// by their own doc comments ("four lines, never a data dump" /
// "compact single-line row") - the Org Chart's job is "where does this
// person sit and what's their e-KYC state," the Dashboard's is "how many
// are waiting," neither is "roster management," which is what the fuller
// Operational Card and drawer are for. Clicking either compact surface
// still opens the same shared drawer, which has the full picture.

/**
 * Prompt 139D, redesigned by Prompt 139D-R2: the Org Chart's compact card
 * for its high-volume worker/applicant panel (a roster that must stay
 * usable at 100k+ real rows, one page at a time - see
 * ../agent/AgentWorkerPanel.jsx's own doc comment). Owner feedback on the
 * 139D version was that the row was too large for an "enterprise, high
 * volume" surface (identity line, a tag+dot+dot meta line, and a full
 * next-step sentence + affordance line - effectively 4 visual rows). This
 * version keeps strictly the fields the redesign spec calls for - name,
 * compact status badge, country/language, one short status line, optional
 * created date - and drops the "อยู่ในสายงาน" tag and the "ดูรายละเอียด →"
 * affordance text entirely (the whole card is the click target; a hover
 * state is the only affordance signal, same convention
 * ApplicantDashboardItem below already uses). Target rendered height is
 * ~64-84px: 3 fixed single-line rows (no wrapping - each row clips with
 * ellipsis in applicantCard.css) so a long name/status can never blow the
 * card past a compact grid tile's own row height. Left-edge accent color
 * now follows the real status variant (amber/blue/green, from the same
 * describeApplicationStatus source every other surface reads) instead of a
 * constant amber - this is the "visually distinct but calm" status signal
 * the redesign asks for, without a solid-fill background on the card.
 */
export function ApplicantOrgRow({ item, onClick }) {
  const status = describeApplicationStatus(item.applicationStatus);
  const languageSuffix = item.preferredLanguageCode ? ` / ${item.preferredLanguageCode}` : "";
  const createdLabel = formatDateTh(item.createdAtUtc);
  return (
    <button type="button" className={`wx-applicant-row wx-applicant-row--${status.badgeVariant}`} onClick={onClick}>
      <div className="wx-applicant-row-top">
        <strong className="wx-applicant-row-name">{item.fullNameNative || item.fullNameEnglish || "-"}</strong>
        <span className={`wx-badge wx-badge--${status.badgeVariant} wx-applicant-row-badge`}>{status.label}</span>
      </div>
      <div className="wx-applicant-row-line">
        <Globe size={11} aria-hidden="true" className="wx-applicant-row-icon" />
        <span className="wx-applicant-row-line-text">
          {item.nationality || "-"}
          {languageSuffix}
        </span>
      </div>
      <div className="wx-applicant-row-line wx-applicant-row-line--status">
        <span className="wx-applicant-row-next">{describeEkycProcessStatus(item.applicationStatus)}</span>
        {createdLabel && (
          <span className="wx-applicant-row-date">
            <Calendar size={10} aria-hidden="true" />
            {createdLabel}
          </span>
        )}
      </div>
    </button>
  );
}

/**
 * Prompt 139D, redesigned by Prompt 139D-R2: the Org Chart worker/applicant
 * panel's dense table/list view row (its compact "Card" view reuses
 * ApplicantOrgRow above unmodified). Adds the two stable columns the
 * redesign spec calls out that the 139D version was missing - phone and
 * source/invite - so Grid view genuinely covers "name, status,
 * country/language, phone, created date, source/invite" without the caller
 * needing to open the drawer just to see a phone number while scanning.
 * Drops the old text "ดูรายละเอียด →" action cell in favor of a small
 * trailing chevron icon (`aria-hidden`, decorative only - the whole row is
 * the real click target via the row `<button>` itself, same as before) so
 * the row stays icon-driven rather than text-heavy at this row density.
 * Same describeApplicationStatus source of truth as every other surface, so
 * a given applicant's status text/color never disagrees between Card and
 * Grid view.
 */
export function ApplicantGridRow({ item, onClick }) {
  const status = describeApplicationStatus(item.applicationStatus);
  const sourceLabel = item.inviteCard?.inviteCodeMasked || "-";
  return (
    <button type="button" className="wx-applicant-grid-row" role="row" onClick={onClick}>
      <span className="wx-applicant-grid-cell wx-applicant-grid-cell--name" role="cell">
        {item.fullNameNative || item.fullNameEnglish || "-"}
      </span>
      <span className="wx-applicant-grid-cell" role="cell">
        <span className={`wx-badge wx-badge--${status.badgeVariant}`}>{status.label}</span>
      </span>
      <span className="wx-applicant-grid-cell wx-applicant-grid-cell--muted" role="cell">
        {item.nationality || "-"}
        {item.preferredLanguageCode ? ` / ${item.preferredLanguageCode}` : ""}
      </span>
      <span className="wx-applicant-grid-cell wx-applicant-grid-cell--muted" role="cell">
        {item.phoneNumber && <Phone size={11} aria-hidden="true" className="wx-applicant-grid-icon" />}
        {item.phoneNumber || "-"}
      </span>
      <span className="wx-applicant-grid-cell wx-applicant-grid-cell--muted" role="cell">{formatDateTh(item.createdAtUtc) ?? "-"}</span>
      <span className="wx-applicant-grid-cell wx-applicant-grid-cell--muted" role="cell">{sourceLabel}</span>
      <span className="wx-applicant-grid-cell wx-applicant-grid-cell--chevron" role="cell" aria-hidden="true">
        <ChevronRight size={14} />
      </span>
    </button>
  );
}

/**
 * Prompt 138Y-C: the Agent Dashboard's own glance-density summary item -
 * deliberately lighter than ApplicantOperationalCard (no contact grid, no
 * Invite Card field, no repeated footer copy). The Dashboard's job is "how
 * many are waiting and what's their state right now," not roster
 * management, so this shows only: name, the live e-KYC status badge, the
 * honest "already in the line" tag, submitted time, and one status line
 * (describeEkycProcessStatus) - four lines, never a data dump. Clicking
 * opens the same shared drawer every other surface uses; this component
 * itself never links anywhere.
 */
export function ApplicantDashboardItem({ item, onClick }) {
  const status = describeApplicationStatus(item.applicationStatus);
  return (
    <button type="button" className="wx-applicant-dash-item" onClick={onClick}>
      <div className="wx-applicant-dash-item-top">
        <strong className="wx-applicant-dash-item-name">{item.fullNameNative || item.fullNameEnglish || "-"}</strong>
        <span className={`wx-badge wx-badge--${status.badgeVariant}`}>{status.label}</span>
      </div>
      <div className="wx-applicant-dash-item-meta">
        <span className="wx-applicant-dash-item-tag">{LINE_MEMBERSHIP_TAG}</span>
        <span className="wx-applicant-dash-item-dot" aria-hidden="true" />
        <span>ส่งเมื่อ {formatDateTh(item.createdAtUtc) ?? "-"}</span>
      </div>
      <p className="wx-applicant-dash-item-next">{describeEkycProcessStatus(item.applicationStatus)}</p>
    </button>
  );
}
